import { filter, find, map } from 'lodash-es'
import { useEffect, useState } from 'react'

import { PageTitle } from '@/components'
import { Button, Modal } from '@/components/ui'
import Loader from '../../../common/components/Loader'
import {
  INotification,
  SettingsGender,
  TicketList,
} from '../../../common/models'
import { Countries } from '../../../core/constants/countries'
import { getSettingsGender } from '../../../core/services/festival-settings'
import {
  getAllCategories,
  getTicketsByCategoryId,
} from '../../../core/services/tickets'
import { MessageBox } from '../messages/MessageBox'

export interface CreateEditModalProps {
  visible: boolean
  setVisible: (show: boolean) => void
  selectedItem?: INotification
}

export const SegmentModal = (
  props: CreateEditModalProps,
): JSX.Element | null => {
  const { visible, setVisible, selectedItem } = props
  const [genders, setGender] = useState<SettingsGender[]>([])
  const [tickets, setTickets] = useState<TicketList[]>([])
  const checkinOptions = [
    { label: 'No Check-in', value: 'NO_CHECKIN' },
    { label: 'Check-in Done', value: 'CHECKIN_DONE' },
  ]
  const topUpOptions = [
    { label: 'My Balance Amount = 0', value: 'AMOUNT_ZERO' },
    { label: 'My Balance Amount = +', value: 'POSITIVE_AMOUNT ' },
  ]

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        if (visible) {
          setIsLoading(true)
          const resp = await getSettingsGender()
          if (resp.status === 200) {
            setGender(resp.data)
          }
          const response2 = await getAllCategories({ size: 100 })
          if (response2.status === 200) {
            const temp: TicketList[] = []
            await Promise.all(map(response2.data.content, async (category) => {
              const resp2 = await getTicketsByCategoryId(category.id)
              if (resp2.status === 200) {
                map(resp2.data.content, (ticket) => {
                  temp.push(ticket)
                })
              }
            }))
            setTickets(temp)
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setIsLoading(false)
      }
    })()
  }, [visible])

  if (!selectedItem) {
    return null
  }

  if (isLoading) {
    return <Loader />
  }

  const handleClose = () => {
    setVisible(false)
  }

  return (
    <>
      <Modal
        open={visible}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Notifications" subtitle="Segments Info" />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-end gap-2 border-t border-monochrome-120 pt-4 mt-4">
            <Button onClick={handleClose} type="button" variant="secondary">
              Done
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3 w-full">
          {selectedItem.countrySegment && (
            <div className="flex flex-col w-full gap-1">
              <span className="text-button-m font-medium text-monochrome-1">
                Country:
              </span>
              <span className="text-body-medium text-monochrome-2">
                {selectedItem.countrySegment.allCountries
                  ? 'All Countries'
                  : map(
                      selectedItem.countrySegment.countries,
                      (option, index) => {
                        return `${
                          find(Countries, (g) => g.countryCode === option)
                            ?.countryNameEn
                        }${
                          index !==
                          selectedItem.countrySegment.countries.length - 1
                            ? ', '
                            : ''
                        }`
                      },
                    )}
              </span>
            </div>
          )}
          {selectedItem.ageSegment && (
            <div className="flex flex-col w-full gap-1">
              <span className="text-button-m font-medium text-monochrome-1">
                Age:
              </span>
              <span className="text-body-medium text-monochrome-2">
                {selectedItem.ageSegment.allAges
                  ? 'All Ages'
                  : map(selectedItem.ageSegment.ages, (option, index) => {
                      return `${option}${
                        index !== selectedItem.ageSegment.ages.length - 1
                          ? ', '
                          : ''
                      }`
                    })}
              </span>
            </div>
          )}
          {selectedItem.genderSegment && (
            <div className="flex flex-col w-full gap-1">
              <span className="text-button-m font-medium text-monochrome-1">
                Gender:
              </span>
              <span className="text-body-medium text-monochrome-2">
                {selectedItem.genderSegment.allGenders
                  ? 'All Genders'
                  : map(selectedItem.genderSegment.genders, (option, index) => {
                      return `${find(genders, (g) => g.id === option)?.name}${
                        index !== selectedItem.genderSegment.genders.length - 1
                          ? ', '
                          : ''
                      }`
                    })}
              </span>
            </div>
          )}
          {selectedItem.checkInSegment && (
            <div className="flex flex-col w-full gap-1">
              <span className="text-button-m font-medium text-monochrome-1">
                Check-in:
              </span>
              <span className="text-body-medium text-monochrome-2">
                {selectedItem.checkInSegment.allCheckIns
                  ? 'All Check-ins'
                  : map(
                      selectedItem.checkInSegment.checkInTypes,
                      (option, index) => {
                        return `${
                          find(checkinOptions, (g) => g.value === option)?.label
                        }${
                          index !==
                          selectedItem.checkInSegment.checkInTypes.length - 1
                            ? ', '
                            : ''
                        }`
                      },
                    )}
              </span>
            </div>
          )}
          {selectedItem.topUpSegment && (
            <div className="flex flex-col w-full gap-1">
              <span className="text-button-m font-medium text-monochrome-1">
                Top-Up:
              </span>
              <span className="text-body-medium text-monochrome-2">
                {selectedItem.topUpSegment.allTopUps
                  ? 'All Top-Ups'
                  : map(
                      selectedItem.topUpSegment.topUpTypes,
                      (option, index) => {
                        return `${
                          find(topUpOptions, (g) => g.value === option)?.label
                        }${
                          index !==
                          selectedItem.topUpSegment.topUpTypes.length - 1
                            ? ', '
                            : ''
                        }`
                      },
                    )}
              </span>
            </div>
          )}
          {selectedItem.ticketTypeSegment && (
            <div className="flex flex-col w-full gap-1">
              <span className="text-button-m font-medium text-monochrome-1">
                Type of Ticket:
              </span>
              <span className="text-body-medium text-monochrome-2">
                {selectedItem.ticketTypeSegment.allTickets
                  ? 'All Types of Tickets'
                  : map(
                      selectedItem.ticketTypeSegment.tickets,
                      (option, index) => {
                        return `${
                          filter(tickets, (t) => t.id === option)?.[0]?.name
                        }${
                          index !==
                          selectedItem.ticketTypeSegment.tickets.length - 1
                            ? ', '
                            : ''
                        }`
                      },
                    )}
              </span>
            </div>
          )}
        </div>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
